import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useTableCtx from "../tableContext/useTableCtx";
import {
  TableActions,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "../tableContext/TableContext";
import { request } from "../../../helpers/apiHelper";

function useTableConfig({
  handleTableActions,
  handleUnmount,
  globalFilter,
  tableHeaderData,
  tableItemsData,
  withRouteParams = true,
}: {
  handleTableActions?: (value: TableActions) => void;
  handleUnmount?: () => void;
  tableHeaderData: TableHeaderCategoryConfig[];
  tableItemsData: TableData[];
  globalFilter?: TableFilterDataItem[];
  withRouteParams?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const [isFirstFetch, setIsFirstFetch] = useState(true);

  const tableContext = useTableCtx()!;
  const {
    globalFilterOptions,
    tableGlobalSearch,
    tablePagination,
    tableSearch,
    debouncedTableContextValues,
    setGlobalFilterOptions,
    setTableData,
    setTableHeader,
    setTableSearch,
  } = tableContext;

  useEffect(() => {
    return () => {
      handleUnmount && handleUnmount();
    };
  }, [handleUnmount]);

  useEffect(() => {
    if (tableHeaderData) {
      setTableSearch((prev: any) => {
        const initialSearch = tableHeaderData.reduce(
          (acc, { value, withSearch, withFilter }) => {
            if (withSearch || withFilter) {
              acc[value] = prev?.[value] ?? ""; // keep existing value or default to ""
            }
            return acc;
          },
          {} as Record<string, string>,
        );
        // only update if something actually changed
        const hasChanges = Object.keys(initialSearch).some(
          (key) => prev?.[key] === undefined,
        );
        if (!hasChanges) return prev;
        return { ...initialSearch, ...prev };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableHeaderData]);

  useEffect(() => {
    tableItemsData && setTableData(tableItemsData);
    tableHeaderData && setTableHeader(tableHeaderData);
    globalFilter && setGlobalFilterOptions(globalFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, tableHeaderData, tableItemsData]);

  useEffect(() => {
    const getTableActions = () => {
      if (handleTableActions && debouncedTableContextValues) {
        handleTableActions({
          ...debouncedTableContextValues,
          globalFilterOptions,
          tableGlobalSearch,
          tableContext,
        });
        setIsFirstFetch(false);
      }
    };
    getTableActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTableContextValues, globalFilterOptions, tableGlobalSearch]);

  useEffect(() => {
    return () => {
      if (isFirstFetch === false) {
        request.cancel();
      }
    };
  }, [isFirstFetch]);

  useEffect(() => {
    if (!withRouteParams || !debouncedTableContextValues) return;
    if (tablePagination) {
      const next = new URLSearchParams(searchParamsRef.current);
      Object.entries({
        ...tableSearch,
        ...tablePagination,
      }).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
      if (next.toString() !== searchParamsRef.current.toString()) {
        setSearchParams(next, { replace: true });
      }
    }
  }, [
    setSearchParams,
    tablePagination,
    tableSearch,
    debouncedTableContextValues,
    withRouteParams,
  ]);
}

export default useTableConfig;
